import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import CopyIcon from "@mui/icons-material/ContentCopyOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import OpenInFullOutlined from "@mui/icons-material/OpenInFullOutlined";
import PaidOutlined from "@mui/icons-material/PaidOutlined";
import { Box, Button, ClickAwayListener, Dialog, DialogActions, DialogContent, DialogTitle, Menu, MenuItem } from "@mui/material";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthProvider";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { FEATURE_FLAGS } from "../../contexts/FeatureFlagsProvider";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useRequestsContext } from "../../contexts/RequestsContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useWeekIndex } from "../../contexts/WeekIndexProvider";
import { useCopyToClipboard } from "../../hooks/useCopyToClipboard";
import { useFeatureFlag } from "../../hooks/useOpenFeatureFlag";
import { useShifts } from "../../hooks/useShifts";
import { showAgencyPostingResult } from "../../utils/agencyWarnings";
import { isConstantBonusSnapshot } from "../../utils/incentive";
import type { BaseRequest, OpenShiftRequest } from "../../utils/models";
import { RequestResponsesStatusOptions } from "../../utils/models";
import { NormalizedRequestApprovalDialog } from "../approvalDialogs/RequestsApproval/NormalizedRequestApprovalDialog";
import { SystemValidationTextField } from "../UIToolkit/Inputs/SystemValidationTextField";
import SystemButton from "../UIToolkit/SystemButton/SystemButton";
import { useUpdateRequestFlatBonus } from "./hooks/useUpdateRequestFlatBonus";

const RequestMessageMenuContent: FC = () => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: 1,
		}}
	>
		<ChatOutlinedIcon />
		Message staff
	</Box>
);

const RequestDeleteMenuContent: FC = () => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: 1,
		}}
	>
		<DeleteIcon />
		Delete Request
	</Box>
);

const CopyRequestID: FC = () => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: 1,
		}}
	>
		<CopyIcon />
		Copy Request ID
	</Box>
);

const RequestApproveMenuContent: FC = () => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: 1,
		}}
	>
		<OpenInFullOutlined />
		Show Responses
	</Box>
);

type RequestBonusMenuContentProps = {
	request: BaseRequest;
};

// "Add" when the shift has no bonus yet, "Edit" when it already has a flat one.
const hasFlatBonus = (request: BaseRequest): boolean => isConstantBonusSnapshot(request.incentive_windows) && (request.incentive_windows?.length ?? 0) > 0;

const RequestBonusMenuContent: FC<RequestBonusMenuContentProps> = ({ request }) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			gap: 1,
		}}
	>
		<PaidOutlined />
		{hasFlatBonus(request) ? "Edit" : "Add"} Bonus
	</Box>
);

// Bonus can be added when the shift has no bonus, or edited when it has a flat one. Decay curves are read-only.
const canAddOrEditFlatBonus = (request: BaseRequest): boolean => isConstantBonusSnapshot(request.incentive_windows);

type RequestUpdateMenuContentProps = {
	requestsCount: number;
};

const RequestUpdateMenuContent: FC<RequestUpdateMenuContentProps> = ({ requestsCount }) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
		}}
	>
		<Edit
			sx={{
				marginRight: "5px",
				cursor: "pointer",
				color: "grey",
			}}
		/>
		Update needed nurses: {requestsCount - 1}
	</Box>
);

type RequestMenuProps = {
	request: BaseRequest;
	shiftNo: number;
	dayNo: number;
	actionsMenuAnchorEl: HTMLElement | null;
	onMenuClose: () => void;
	requestsCount: number;
	onUpdateRequest?: (updateFunc: (request: OpenShiftRequest) => OpenShiftRequest) => void;
	onMessageClick?: (request: BaseRequest) => void;
};

export const RequestMenu: FC<RequestMenuProps> = ({
	request,
	requestsCount,
	shiftNo,
	dayNo,
	onMenuClose,
	actionsMenuAnchorEl,
	onUpdateRequest,
	onMessageClick,
}) => {
	const { t } = useTranslation();
	const { getShiftDate, hospitalData, showErrorSnackbar, chosenDepartment } = useHospitalData();
	const { track } = useAnalytics();
	const { weekIndex } = useWeekIndex();
	const { showSnackbar } = useSnackbar();
	const { refreshRequests, setStaffRequests, staffRequests } = useRequestsContext();
	const { patchShift, deleteShift } = useShifts();
	const { userInfo } = useAuth();
	const { copyToClipboard } = useCopyToClipboard();

	const shiftType = useMemo<string>(() => hospitalData?.weeks[weekIndex]?.weeklyShifts[shiftNo]?.type || "", [hospitalData?.weeks, weekIndex, shiftNo]);
	const isShiftBonusesEnabled = useFeatureFlag(FEATURE_FLAGS.EnableShiftBonuses);
	const { updateFlatBonus } = useUpdateRequestFlatBonus();

	const [isOpenApprovalDialogOpen, setIsOpenApprovalDialogOpen] = useState<boolean>(false);
	const [deleteConfirmation, setDeleteConfirmation] = useState<boolean>(false);
	const [isDeleting, setIsDeleting] = useState<boolean>(false);
	const [bonusMenuAnchorEl, setBonusMenuAnchorEl] = useState<HTMLElement | null>(null);
	const [bonusValue, setBonusValue] = useState<string>((request.current_bonus ?? request.bonus)?.toString() || "");

	useEffect(() => {
		if (actionsMenuAnchorEl) {
			track("request actions menu opened", {
				request_id: request.id,
				day_no: dayNo,
				shift_no: shiftNo,
				week_index: weekIndex,
				date: getShiftDate(weekIndex, shiftNo, dayNo).toFormat("yyyy-MM-dd"),
				shift_type: shiftType,
			});
		}
	}, [actionsMenuAnchorEl, request.id, dayNo, shiftNo, weekIndex, getShiftDate, shiftType, track]);

	const handleActionsMenuClose = (): void => onMenuClose?.();

	const handleDeleteRequest = async (): Promise<void> => {
		setIsDeleting(true);
		try {
			track("Day Schedule - delete request", {
				source: "day_schedule",
				request_id: request.id,
				shift_no: shiftNo,
				day_no: dayNo,
				date: getShiftDate(weekIndex, shiftNo, dayNo).toFormat("MM/DD/yyyy"),
			});
			const result = await deleteShift({
				shiftId: request.id,
				departmentId: chosenDepartment.department_id,
			});

			setStaffRequests(staffRequests.filter((r) => r.id !== request.id));
			track("Day Schedule - request delete", {
				source: "day_schedule",
				request_id: request.id,
				shift_no: shiftNo,
				day_no: dayNo,
				date: getShiftDate(weekIndex, shiftNo, dayNo).toFormat("MM/DD/yyyy"),
			});
			showAgencyPostingResult(showSnackbar, t("success.deleted.open.shift"), t, result?.agency_posting_failures);
			setDeleteConfirmation(false);
			handleActionsMenuClose();
		} catch (error) {
			showErrorSnackbar("Failed to delete request", { action: "deleted request failed", request_id: request.id }, error);
		} finally {
			setIsDeleting(false);
		}
	};

	const handleUpdateRequest = async (): Promise<void> => {
		try {
			track("Day Schedule - update request", {
				source: "day_schedule",
				request_id: request.id,
				updated_needed_nurses: requestsCount - 1,
			});
			const result = await patchShift({
				shiftId: request.id,
				neededNurses: requestsCount - 1,
				recommendationMerge: false,
			});

			onUpdateRequest?.((request: OpenShiftRequest) => ({
				...request,
				needed_nurses: requestsCount - 1,
			}));
			showAgencyPostingResult(showSnackbar, t("success.needed.nurses.updated"), t, result?.agency_posting_failures);
			await refreshRequests();
			handleActionsMenuClose();
		} catch (error) {
			showErrorSnackbar(
				"Failed to update needed nurses",
				{
					action: "updated needed nurses failed from day schedule",
					request_id: request.id,
				},
				error,
			);
		}
	};

	const handleCloseApprovalDialog = (): void => {
		setIsOpenApprovalDialogOpen(false);
		handleActionsMenuClose();
	};

	const handleBonusClick = (event: React.MouseEvent<HTMLElement>): void => {
		setBonusMenuAnchorEl(event.currentTarget);
	};

	const handleBonusMenuClose = (): void => {
		setBonusMenuAnchorEl(null);
		setBonusValue((request.current_bonus ?? request.bonus)?.toString() || "");
	};

	const handleBonusSave = async (): Promise<void> => {
		const bonusAmount = bonusValue ? parseFloat(bonusValue) : 0;
		track("Day Schedule - update request bonus", {
			source: "day_schedule",
			request_id: request.id,
			bonus_amount: bonusAmount,
		});
		await updateFlatBonus({
			request,
			bonusAmount,
			onLocalUpdate: (patch) => onUpdateRequest?.((req: OpenShiftRequest) => ({ ...req, ...patch })),
			onDone: () => {
				setBonusMenuAnchorEl(null);
				handleActionsMenuClose();
			},
		});
	};

	const copyRequestId = useCallback(async () => {
		const success = await copyToClipboard(request.id);
		if (success) {
			showSnackbar("Request ID copied to clipboard", "success");
		} else {
			showErrorSnackbar("Failed to copy to clipboard", {});
		}
		onMenuClose();
	}, [request.id, copyToClipboard, showSnackbar, showErrorSnackbar, onMenuClose]);

	const handleDeleteBonus = async (): Promise<void> => {
		track("Day Schedule - delete request bonus", {
			source: "day_schedule",
			request_id: request.id,
		});
		// Same code path as Save with $0 — runs the overlap/merge prompt and routes to the delete endpoint.
		await updateFlatBonus({
			request,
			bonusAmount: 0,
			onLocalUpdate: (patch) => onUpdateRequest?.((req: OpenShiftRequest) => ({ ...req, ...patch })),
			onDone: () => {
				setBonusMenuAnchorEl(null);
				handleActionsMenuClose();
			},
		});
	};

	const handleApproveRequest = async (): Promise<void> => {
		track("Day Schedule - approve request", {
			source: "day_schedule",
			request_id: request.id,
			shift_no: shiftNo,
			day_no: dayNo,
			date: getShiftDate(weekIndex, shiftNo, dayNo).toFormat("MM/DD/yyyy"),
		});
		setIsOpenApprovalDialogOpen(true);
	};

	const handleMessageClick = (): void => {
		onMessageClick?.(request);
		handleActionsMenuClose();
	};

	return (
		<>
			<ClickAwayListener onClickAway={() => {}}>
				<Menu
					data-testid={`request-menu-${request.id}`}
					anchorEl={actionsMenuAnchorEl}
					open={Boolean(actionsMenuAnchorEl)}
					onClose={handleActionsMenuClose}
					anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
					transformOrigin={{ vertical: "top", horizontal: "right" }}
					slotProps={{
						paper: {
							sx: {
								mt: 1,
							},
						},
					}}
				>
					{request.message.request_data.length > 0 && request.message.request_data.some((r) => r.status === RequestResponsesStatusOptions.PENDING) && (
						<MenuItem data-testid={`request-approve-${request.id}`} onClick={handleApproveRequest}>
							<RequestApproveMenuContent />
						</MenuItem>
					)}
					{requestsCount === 1 ? (
						<MenuItem
							data-testid={`request-reject-${request.id}`}
							onClick={() => {
								setDeleteConfirmation(true);
							}}
						>
							<RequestDeleteMenuContent />
						</MenuItem>
					) : (
						<MenuItem data-testid={`request-update-${request.id}`} onClick={handleUpdateRequest}>
							<RequestUpdateMenuContent requestsCount={requestsCount} />
						</MenuItem>
					)}
					<MenuItem data-testid={`request-message-${request.id}`} onClick={handleMessageClick}>
						<RequestMessageMenuContent />
					</MenuItem>
					{isShiftBonusesEnabled && canAddOrEditFlatBonus(request) && (
						<MenuItem data-testid={`request-bonus-${request.id}`} onClick={handleBonusClick}>
							<RequestBonusMenuContent request={request} />
						</MenuItem>
					)}
					{userInfo.support && (
						<MenuItem data-testid={`request-copy-id-${request.id}`} onClick={copyRequestId}>
							<CopyRequestID />
						</MenuItem>
					)}
				</Menu>
			</ClickAwayListener>

			<NormalizedRequestApprovalDialog
				open={isOpenApprovalDialogOpen}
				onClose={handleCloseApprovalDialog}
				request={request as OpenShiftRequest}
				shouldNotAllowToMinimize={true}
			/>

			<Menu
				data-testid={`request-bonus-menu-${request.id}`}
				anchorEl={bonusMenuAnchorEl}
				open={Boolean(bonusMenuAnchorEl)}
				onClose={handleBonusMenuClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				slotProps={{
					paper: {
						sx: {
							mt: 1,
							p: 2,
							minWidth: 250,
						},
					},
				}}
			>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					<SystemValidationTextField
						dataTestId="request-bonus-amount-input"
						label="Bonus Amount ($)"
						inputType="number"
						variant="outlined"
						value={bonusValue}
						onChange={(e) => setBonusValue(e.target.value)}
						maxValue={1000}
						defaultHelperText="Enter bonus amount"
					/>
					<Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
						<Button data-testid="request-bonus-cancel-button" size="small" onClick={handleBonusMenuClose}>
							Cancel
						</Button>
						{!!(request.current_bonus ?? request.bonus) && (
							<Button data-testid="request-bonus-delete-button" size="small" onClick={handleDeleteBonus} color="error">
								Delete Bonus
							</Button>
						)}
						<Button data-testid="request-bonus-save-button" size="small" onClick={handleBonusSave} variant="contained">
							{(request.current_bonus ?? request.bonus) ? "Update" : "Add"}
						</Button>
					</Box>
				</Box>
			</Menu>

			<Dialog data-testid="request-delete-dialog" open={deleteConfirmation} onClose={() => setDeleteConfirmation(false)}>
				<DialogTitle>{t("delete.open.shift.title")}</DialogTitle>
				<DialogContent>{t("delete.open.shift.warning")}</DialogContent>
				<DialogActions>
					<SystemButton data-testid="request-delete-cancel-button" buttonVariant="secondary" onClick={() => setDeleteConfirmation(false)} disabled={isDeleting}>
						{t("cancel")}
					</SystemButton>
					<SystemButton
						data-testid="request-delete-confirm-button"
						buttonVariant="delete"
						onClick={handleDeleteRequest}
						disabled={isDeleting}
						loading={isDeleting}
					>
						{t("delete.request")}
					</SystemButton>
				</DialogActions>
			</Dialog>
		</>
	);
};
